import { type Infer, v } from 'convex/values'

import { roundAmount } from './habitMeasure'
import { plateStep } from './levelUp'

// Workout session plans on a habit: a few named sessions of items (sets × reps,
// timed, distance) that rotate one per finished session. Pure helpers shared by
// sessionPlans.ts, the plan generator and the clients.

export const MAX_SESSIONS = 6
export const MAX_ITEMS = 15
export const MAX_NAME = 60
export const MAX_CUE = 100
export const MAX_UNIT = 8
export const MAX_DISMISSED = 40
// Done sessions read back for progression, PRs and the coach line.
export const HISTORY = 12

export const itemKindValidator = v.union(
  v.literal('sets'),
  v.literal('timed'),
  v.literal('distance'),
)
export type ItemKind = Infer<typeof itemKindValidator>

export const planItemValidator = v.object({
  id: v.string(),
  name: v.string(),
  kind: itemKindValidator,
  sets: v.number(),
  reps: v.optional(v.number()),
  weight: v.optional(v.number()),
  seconds: v.optional(v.number()),
  distance: v.optional(v.number()),
  // kg / lb for sets, km / mi / m for distance.
  unit: v.optional(v.string()),
  cue: v.optional(v.string()),
})
export type PlanItem = Infer<typeof planItemValidator>

export const planSessionValidator = v.object({
  id: v.string(),
  name: v.string(),
  items: v.array(planItemValidator),
})
export type PlanSession = Infer<typeof planSessionValidator>

export const planProfileValidator = v.object({
  level: v.optional(
    v.union(v.literal('beginner'), v.literal('intermediate'), v.literal('advanced')),
  ),
  equipment: v.optional(v.union(v.literal('none'), v.literal('dumbbells'), v.literal('gym'))),
  minutes: v.optional(v.number()),
})
export type PlanProfile = Infer<typeof planProfileValidator>

export const setLogValidator = v.object({
  done: v.boolean(),
  reps: v.optional(v.number()),
  weight: v.optional(v.number()),
  seconds: v.optional(v.number()),
  distance: v.optional(v.number()),
})
export type SetLog = Infer<typeof setLogValidator>

export const itemLogValidator = v.object({
  itemId: v.string(),
  name: v.string(),
  kind: itemKindValidator,
  unit: v.optional(v.string()),
  // The plan's target when the session started; sets hold what was actually done.
  target: v.object({
    reps: v.optional(v.number()),
    weight: v.optional(v.number()),
    seconds: v.optional(v.number()),
    distance: v.optional(v.number()),
  }),
  sets: v.array(setLogValidator),
})
export type ItemLog = Infer<typeof itemLogValidator>

// Denormalised on the habit node for row meta lines ("Push day · 5 exercises").
export const planSummaryValidator = v.object({ name: v.string(), items: v.number() })

const clampNum = (n: number | undefined, min: number, max: number) =>
  n === undefined || !Number.isFinite(n) ? undefined : roundAmount(Math.min(max, Math.max(min, n)))

const clip = (s: string | undefined, max: number) =>
  (s ?? '').replace(/\s+/g, ' ').trim().slice(0, max)

export const newId = () => Math.random().toString(36).slice(2, 10)

export function cleanItem(raw: PlanItem): PlanItem | null {
  const name = clip(raw.name, MAX_NAME)
  if (!name) return null
  const kind = raw.kind
  const sets = Math.round(clampNum(raw.sets, 1, 10) ?? 1)
  const cue = clip(raw.cue, MAX_CUE)
  const unit = clip(raw.unit, MAX_UNIT).toLowerCase()
  const item: PlanItem = { id: clip(raw.id, 20) || newId(), name, kind, sets }
  if (cue) item.cue = cue
  if (kind === 'sets') {
    item.reps = Math.round(clampNum(raw.reps, 1, 200) ?? 10)
    const weight = clampNum(raw.weight, 0, 1000)
    if (weight) {
      item.weight = weight
      item.unit = unit === 'lb' || unit === 'lbs' ? 'lb' : 'kg'
    }
  } else if (kind === 'timed') {
    item.seconds = Math.round(clampNum(raw.seconds, 5, 4 * 3600) ?? 60)
  } else {
    item.distance = clampNum(raw.distance, 0.01, 1000) ?? 1
    item.unit = unit === 'mi' || unit === 'm' ? unit : 'km'
  }
  return item
}

export function cleanSessions(raw: PlanSession[]): PlanSession[] {
  const ids = new Set<string>()
  const out: PlanSession[] = []
  for (const s of raw.slice(0, MAX_SESSIONS)) {
    const items: PlanItem[] = []
    for (const it of s.items) {
      const c = cleanItem(it)
      if (!c) continue
      if (ids.has(c.id)) c.id = newId()
      ids.add(c.id)
      items.push(c)
      if (items.length >= MAX_ITEMS) break
    }
    if (!items.length) continue
    let id = clip(s.id, 20) || newId()
    if (ids.has(id)) id = newId()
    ids.add(id)
    out.push({ id, name: clip(s.name, MAX_NAME), items })
  }
  return out
}

export const nextIndex = (sessions: readonly PlanSession[], next: number) =>
  sessions.length ? ((next % sessions.length) + sessions.length) % sessions.length : 0

export const summaryOf = (sessions: readonly PlanSession[], next: number) => {
  const s = sessions[nextIndex(sessions, next)]
  return s ? { name: s.name, items: s.items.length } : undefined
}

export function itemLogFrom(item: PlanItem): ItemLog {
  const target = {
    ...(item.reps !== undefined && { reps: item.reps }),
    ...(item.weight !== undefined && { weight: item.weight }),
    ...(item.seconds !== undefined && { seconds: item.seconds }),
    ...(item.distance !== undefined && { distance: item.distance }),
  }
  return {
    itemId: item.id,
    name: item.name,
    kind: item.kind,
    ...(item.unit && { unit: item.unit }),
    target,
    sets: Array.from({ length: item.sets }, () => ({ done: false, ...target })),
  }
}

export const itemDone = (l: ItemLog) => l.sets.every((s) => s.done)

// Every set done at or above the item's current plan target.
export function isClean(l: ItemLog, item: PlanItem): boolean {
  if (l.sets.length < item.sets || !itemDone(l)) return false
  return l.sets.every(
    (s) =>
      (item.reps === undefined || (s.reps ?? 0) >= item.reps) &&
      (item.weight === undefined || (s.weight ?? 0) >= item.weight) &&
      (item.seconds === undefined || (s.seconds ?? 0) >= item.seconds) &&
      (item.distance === undefined || (s.distance ?? 0) >= item.distance),
  )
}

export type Progression = {
  itemId: string
  name: string
  field: 'weight' | 'reps' | 'seconds' | 'distance'
  from: number
  to: number
  unit?: string
}

export const progressionKey = (p: Pick<Progression, 'itemId' | 'field' | 'from'>) =>
  `${p.itemId}:${p.field}:${p.from}`

// The next step for an item: plate-sized for weights (as number-habit level-ups),
// +1 rep, +5 s on short holds (+1 min on longer timed work), ~10% on distance.
export function progressionStep(item: PlanItem): Omit<Progression, 'name'> | null {
  if (item.kind === 'sets') {
    if (item.weight) {
      const step = plateStep(item.unit ?? 'kg', item.weight) ?? 2.5
      return {
        itemId: item.id,
        field: 'weight',
        from: item.weight,
        to: roundAmount(item.weight + step),
        unit: item.unit,
      }
    }
    if (item.reps === undefined || item.reps >= 200) return null
    return { itemId: item.id, field: 'reps', from: item.reps, to: item.reps + 1 }
  }
  if (item.kind === 'timed') {
    if (item.seconds === undefined) return null
    const step = item.seconds < 120 ? 5 : 60
    return { itemId: item.id, field: 'seconds', from: item.seconds, to: item.seconds + step }
  }
  if (item.distance === undefined) return null
  const step = Math.max(0.1, Math.round(item.distance) / 10)
  return {
    itemId: item.id,
    field: 'distance',
    from: item.distance,
    to: roundAmount(item.distance + step),
    unit: item.unit,
  }
}

// Two clean sessions in a row for an item (newest first in `done`) suggest a step up.
export function progressionsFor(
  session: PlanSession,
  done: readonly { items: ItemLog[] }[],
  dismissed: readonly string[],
): Progression[] {
  const out: Progression[] = []
  for (const item of session.items) {
    const logs = done
      .map((d) => d.items.find((l) => l.itemId === item.id))
      .filter((l): l is ItemLog => !!l)
      .slice(0, 2)
    if (logs.length < 2 || !logs.every((l) => isClean(l, item))) continue
    const step = progressionStep(item)
    if (!step || dismissed.includes(progressionKey(step))) continue
    out.push({ ...step, name: item.name })
  }
  return out
}

const bestOf = (l: ItemLog) => {
  const done = l.sets.filter((s) => s.done)
  if (!done.length) return 0
  if (l.kind === 'sets') return Math.max(...done.map((s) => s.weight ?? 0))
  if (l.kind === 'timed') return Math.max(...done.map((s) => s.seconds ?? 0))
  return Math.max(...done.map((s) => s.distance ?? 0))
}

// Items beating every earlier logged best (weight for lifts, hold/time, distance).
export function personalRecords(
  items: readonly ItemLog[],
  earlier: readonly { items: ItemLog[] }[],
) {
  const prs: string[] = []
  for (const l of items) {
    const best = bestOf(l)
    if (!best) continue
    const prev = earlier
      .map((d) => d.items.find((x) => x.itemId === l.itemId))
      .filter((x): x is ItemLog => !!x)
    if (!prev.length) continue
    if (best > Math.max(...prev.map(bestOf)))
      prs.push(`${l.name} ${targetText(l.kind, best, l.unit)}`)
  }
  return prs
}

export function targetText(kind: ItemKind, n: number, unit?: string) {
  if (kind === 'sets') return `${n} ${unit ?? 'kg'}`
  if (kind === 'timed') return n >= 60 && n % 60 === 0 ? `${n / 60} min` : `${n} s`
  return `${n} ${unit ?? 'km'}`
}

export function itemText(i: PlanItem) {
  if (i.kind === 'sets')
    return `${i.name} ${i.sets}×${i.reps ?? 0}${i.weight ? ` @ ${i.weight} ${i.unit ?? 'kg'}` : ''}`
  if (i.kind === 'timed')
    return `${i.name} ${i.sets > 1 ? `${i.sets}×` : ''}${targetText('timed', i.seconds ?? 0)}`
  return `${i.name} ${i.sets > 1 ? `${i.sets}×` : ''}${targetText('distance', i.distance ?? 0, i.unit)}`
}

// One compact coach line per planned habit.
export function coachLine(input: {
  title: string
  sessions: readonly PlanSession[]
  next: number
  recent: readonly { date: string; sessionName: string; items: ItemLog[]; prs?: string[] }[]
}): string {
  const { sessions, recent } = input
  const names = sessions.map((s) => s.name || 'session')
  const nextS = sessions[nextIndex(sessions, input.next)]
  const parts = [
    `${input.title}: ${sessions.length > 1 ? `rotation ${names.join('/')}, next ${nextS?.name}` : `${nextS?.items.length ?? 0} items`}`,
  ]
  const last = recent[0]
  if (last) {
    const skipped = last.items.filter((l) => !itemDone(l)).map((l) => l.name)
    const done = last.items.length - skipped.length
    parts.push(
      `last ${last.sessionName || 'session'} ${last.date}: ${done}/${last.items.length} done${skipped.length ? `, skipped ${skipped.join(', ')}` : ''}${last.prs?.length ? `, PR ${last.prs.join(', ')}` : ''}`,
    )
  }
  const counts = new Map<string, number>()
  for (const r of recent)
    for (const l of r.items) if (!itemDone(l)) counts.set(l.name, (counts.get(l.name) ?? 0) + 1)
  const often = [...counts].filter(([, n]) => n >= 2).map(([name, n]) => `${name} ${n}×`)
  if (often.length) parts.push(`often skipped lately: ${often.slice(0, 3).join(', ')}`)
  return parts.join('; ')
}

export const sessionPlanCoachNote = (lines: string) =>
  lines
    ? `Workout session plans (habit: rotation, last session, skips): ${lines}. If it fits, mention a pattern (e.g. a session or exercise they keep skipping) once, kindly; no medical advice.`
    : ''

// Plan generation prompt + JSON schema (strict: every field present, nulls allowed).
export const PLAN_JSON_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['sessions'],
  properties: {
    sessions: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['name', 'items'],
        properties: {
          name: { type: 'string' },
          items: {
            type: 'array',
            items: {
              type: 'object',
              additionalProperties: false,
              required: [
                'name',
                'kind',
                'sets',
                'reps',
                'weight',
                'seconds',
                'distance',
                'unit',
                'cue',
              ],
              properties: {
                name: { type: 'string' },
                kind: { type: 'string', enum: ['sets', 'timed', 'distance'] },
                sets: { type: 'number' },
                reps: { type: ['number', 'null'] },
                weight: { type: ['number', 'null'] },
                seconds: { type: ['number', 'null'] },
                distance: { type: ['number', 'null'] },
                unit: { type: ['string', 'null'] },
                cue: { type: 'string' },
              },
            },
          },
        },
      },
    },
  },
} as const

export type GeneratedPlan = {
  sessions: Array<{
    name: string
    items: Array<{
      name: string
      kind: string
      sets: number
      reps: number | null
      weight: number | null
      seconds: number | null
      distance: number | null
      unit: string | null
      cue: string
    }>
  }>
}

export function fromGenerated(g: GeneratedPlan): PlanSession[] {
  const kinds = new Set(['sets', 'timed', 'distance'])
  return cleanSessions(
    (g.sessions ?? []).map((s) => ({
      id: newId(),
      name: s.name ?? '',
      items: (s.items ?? [])
        .filter((i) => kinds.has(i.kind))
        .map((i) => ({
          id: newId(),
          name: i.name,
          kind: i.kind as ItemKind,
          sets: i.sets,
          reps: i.reps ?? undefined,
          weight: i.weight ?? undefined,
          seconds: i.seconds ?? undefined,
          distance: i.distance ?? undefined,
          unit: i.unit ?? undefined,
          cue: i.cue,
        })),
    })),
  )
}

export function planPrompt(input: {
  title: string
  description?: string
  level: string
  equipment: string
  minutes: number
  daysPerWeek?: number
  obstacle?: string
  language: string
  weightUnit: 'kg' | 'lb'
}) {
  return [
    `Write a simple workout session plan for the habit "${input.title}".`,
    input.description ? `Habit note: ${input.description.slice(0, 300)}` : '',
    `Level: ${input.level}. Equipment: ${input.equipment === 'none' ? 'bodyweight only' : input.equipment === 'dumbbells' ? 'a pair of dumbbells at home' : 'a full gym'}. Time per session: about ${input.minutes} minutes.`,
    input.daysPerWeek ? `They train about ${input.daysPerWeek} days a week.` : '',
    input.obstacle
      ? `What has stopped them before: ${input.obstacle.slice(0, 200)}. Keep it realistic for that.`
      : '',
    `Rules: 1 session if the habit is a single routine (e.g. a daily run or plank); 2-3 named sessions that rotate (e.g. "Push day", "Pull day", "Legs day", or "Full body A/B") for strength training 3+ days a week. 3-7 items per session, fitting the time.`,
    `Each item: kind "sets" (sets, reps, weight in ${input.weightUnit} or null for bodyweight), "timed" (sets, seconds per set; e.g. plank 3 sets of 45 seconds, run 1 set of 1200 seconds) or "distance" (sets 1, distance, unit km or mi). Leave fields that don't apply null. Conservative starting loads for the level; beginners mostly bodyweight or light.`,
    `cue: one short form cue (max 12 words), practical, no medical claims.`,
    `Write names and cues in ${input.language}.`,
  ]
    .filter(Boolean)
    .join('\n')
}
